import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, orderBy, Timestamp, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/services/firebase/firebase';
import { Client } from '@/types';

const clientsCol = (uid: string) => collection(db, 'clients', uid, 'items');

export const createClient = async (clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  const docRef = doc(clientsCol(user.uid));
  const newClient = {
    ...clientData,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  await setDoc(docRef, newClient);
  return docRef.id;
};

export const getClients = async (): Promise<Client[]> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  const snap = await getDocs(query(clientsCol(user.uid), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    createdAt: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updatedAt: d.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    lastContact: d.data().lastContact?.toDate?.()?.toISOString() || null
  } as Client));
};

export const getClientById = async (clientId: string): Promise<Client | null> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  const docRef = doc(clientsCol(user.uid), clientId);
  const snap = await getDoc(docRef);
  
  if (!snap.exists()) return null;
  
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    lastContact: data.lastContact?.toDate?.()?.toISOString() || null
  } as Client;
};

export const updateClient = async (clientId: string, updates: Partial<Client>): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  const docRef = doc(clientsCol(user.uid), clientId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

export const deleteClient = async (clientId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('User not authenticated');
  
  const docRef = doc(clientsCol(user.uid), clientId);
  await deleteDoc(docRef);
};

export const searchClients = async (searchTerm: string): Promise<Client[]> => {
  // For now, we'll get all clients and filter client-side
  // In production, you might want to use Algolia or similar for better search
  const allClients = await getClients();
  const term = searchTerm.toLowerCase();
  
  return allClients.filter(client => {
    const fullName = `${client.personalInfo.firstName} ${client.personalInfo.lastName}`.toLowerCase();
    const email = client.contactInfo.email.toLowerCase();
    const phone = client.contactInfo.phone;
    
    return fullName.includes(term) || email.includes(term) || phone.includes(term);
  });
};

export const onClientsSnapshot = (callback: (clients: Client[]) => void) => {
  const user = auth.currentUser;
  if (!user) {
   
    callback([]); // Return empty array
    return () => {}; // Return empty unsubscribe function
  }
  
  const q = query(clientsCol(user.uid), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      const clients = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: d.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        lastContact: d.data().lastContact?.toDate?.()?.toISOString() || null
      } as Client));
      callback(clients);
    },
    (error) => {
      console.error('Error in clients snapshot:', error);
      callback([]); // Return empty array on error
    }
  );
};

export type { Client } from '@/types'; 